import { useLazyQuery } from "@apollo/client";
import { Alert, Collapse, DialogContentText } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useExcludeSeriesOccurrences } from "hooks/useSubmissionMutations";
import pluralize from "pluralize";
import { SUBMISSION_REVISION_CHECK_QUERY } from "queries/submissions";
import { useEffect, useMemo } from "react";

import { Row } from "./types";
import { partitionSubmissions } from "./utils";

type ExcludeOccurrencesConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function ExcludeOccurrencesConfirmationDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ExcludeOccurrencesConfirmationDialogProps) {
  const alerts = useAlerts();
  const [excludeSeriesOccurrences, { loading }] = useExcludeSeriesOccurrences();
  const [checkRevisions, { data: revisionData, loading: revisionCheckLoading }] = useLazyQuery(
    SUBMISSION_REVISION_CHECK_QUERY
  );

  const { virtualEventIds, uniqueSubmissionIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );
  const totalCount = virtualEventIds.length + uniqueSubmissionIds.length;

  // Excluding a materialized occurrence deletes its submission, so block it when
  // the submission has linked revisions (same guard as delete).
  useEffect(() => {
    if (open && uniqueSubmissionIds.length > 0) {
      checkRevisions({ variables: { submissionIds: uniqueSubmissionIds } });
    }
  }, [open, uniqueSubmissionIds, checkRevisions]);

  const submissionsWithRevisions = useMemo(
    () =>
      revisionData?.getRevisionSubmissions?.filter((s) => s.revisionSubmissions.length > 0) ?? [],
    [revisionData]
  );

  const hasRevisions = submissionsWithRevisions.length > 0;

  const handleConfirm = async () => {
    try {
      await excludeSeriesOccurrences({
        variables: {
          input: {
            submissionIds: uniqueSubmissionIds,
            virtualEventIds,
          },
        },
      });

      alerts.success("Occurrence(s) excluded from schedule");
      onSuccess();
      onClose();
    } catch (error) {
      alerts.error("Failed to exclude occurrence(s)", error);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      loading={loading}
      onClose={onClose}
      header="Exclude from Schedule"
      disableConfirm={hasRevisions || revisionCheckLoading}
      onConfirm={handleConfirm}
      confirmText="Exclude"
    >
      <DialogContentText>
        {`This removes the selected ${totalCount > 1 ? totalCount : ""} ${pluralize(
          "occurrence",
          totalCount
        )} from their recurring schedule so they no longer appear. Any submission for an excluded date will be deleted. To re-schedule the occurrence, re-add it from the schedule.`}
      </DialogContentText>
      <Collapse in={hasRevisions} sx={{ mt: 2 }}>
        <Alert severity="error">
          {submissionsWithRevisions.length === 1
            ? `"${submissionsWithRevisions[0].title ?? "Untitled Submission"}" has linked revision submissions and cannot be excluded. Please delete the revision submissions first.`
            : `${submissionsWithRevisions.length} of the selected submissions have linked revision submissions and cannot be excluded. Please delete the revision submissions first.`}
        </Alert>
      </Collapse>
    </ConfirmDialog>
  );
}
